import {
  useQueryActivityCategoryListAsMember,
  useQueryActivityCategoryListAsPublic,
  useQueryAppointableCategoriesAsMember,
  useQueryAppointableCategoriesAsPublic,
} from "@gymflow/api";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import useGymflowModels from "apps/portal/src/store";
import React, { useContext } from "react";

import { LabeledForm } from "../../../atoms";
import { BadgePicker } from "../../../atoms/BadgePicker";
import { HostedPagesFilterPopover } from "../../components/HostedPagesFilterPopover";

export type CreditPackType = "CLASSES" | "APPOINTMENTS";
export type HostedPagesCreditPackFilterPopoverProps = {
  filter: {
    appointableCategories: number[];
    classCategories: number[];
  };
  onChange: (
    newFilter: HostedPagesCreditPackFilterPopoverProps["filter"],
  ) => void;
  type: CreditPackType;
};

export const HostedPagesCreditPackFilterPopover: React.FC<
  HostedPagesCreditPackFilterPopoverProps
> = ({ filter, onChange, type }) => {
  const { userId: id } = useContext(AuthenticationContext);
  const { api } = useGymflowModels();
  const useQueryAppointableCategoriesQueryOptions: Parameters<
    typeof useQueryAppointableCategoriesAsPublic
  >[0] = {
    api,
    paginationOption: { page: 0, size: 200 },
    filter: {
      statusList: ["ACTIVE"],
    },
  };
  const appointableCategoriesQueryAsPublic =
    useQueryAppointableCategoriesAsPublic(
      useQueryAppointableCategoriesQueryOptions,
      { enabled: !id },
    );
  const appointableCategoriesQueryAsMember =
    useQueryAppointableCategoriesAsMember(
      useQueryAppointableCategoriesQueryOptions,
      { enabled: !!id },
    );

  const { data: appointableCategories } = !!id
    ? appointableCategoriesQueryAsMember
    : appointableCategoriesQueryAsPublic;

  const activityCategoryQueryOptions: Parameters<
    typeof useQueryActivityCategoryListAsMember
  >[0] = {
    api,
    filter: {
      size: 200,
      statusList: ["ACTIVE"],
    },
  };
  const activityCategoryListAsMember = useQueryActivityCategoryListAsMember(
    activityCategoryQueryOptions,
    { enabled: !!id },
  );
  const activityCategoryListAsPublic = useQueryActivityCategoryListAsPublic(
    activityCategoryQueryOptions,
    { enabled: !id },
  );
  const { data: classCategories } = !!id
    ? activityCategoryListAsMember
    : activityCategoryListAsPublic;
  return (
    <HostedPagesFilterPopover.Container>
      <HostedPagesFilterPopover.Trigger
        isFilterEmpty={
          type === "CLASSES"
            ? filter.classCategories.length === 0
            : filter.appointableCategories.length === 0
        }
      />
      <HostedPagesFilterPopover.Content>
        <HostedPagesFilterPopover.ContentHeader
          isFilterEmpty={
            type === "CLASSES"
              ? filter.classCategories.length === 0
              : filter.appointableCategories.length === 0
          }
          onReset={() => {
            onChange({
              appointableCategories: [],
              classCategories: [],
            });
          }}
        />
        <div className="flex max-w-full p-4 pt-0">
          {type === "APPOINTMENTS" ? (
            <LabeledForm label="Appointable Category" className="gap-y-2">
              <BadgePicker
                className="max-w-[80vw] lg:max-w-full"
                isMulti
                hasAllOption
                options={
                  appointableCategories?.content.map((e) => ({
                    label: e.name,
                    value: e.id,
                  })) ?? []
                }
                onChange={(newValue) => {
                  onChange({
                    ...filter,
                    appointableCategories: newValue,
                  });
                }}
                value={filter.appointableCategories}
              />
            </LabeledForm>
          ) : (
            <LabeledForm label="Class Category" className="gap-y-2">
              <BadgePicker
                className="max-w-[80vw] lg:max-w-full"
                isMulti
                hasAllOption
                options={
                  classCategories?.content.map((e) => ({
                    label: e.name,
                    value: e.id,
                  })) ?? []
                }
                onChange={(newValue) => {
                  onChange({
                    ...filter,
                    classCategories: newValue,
                  });
                }}
                value={filter.classCategories}
              />
            </LabeledForm>
          )}
        </div>
      </HostedPagesFilterPopover.Content>
    </HostedPagesFilterPopover.Container>
  );
};
